import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { supabase } from '../services/supabaseClient';
import Footer from '../components/layout/Footer';

export default function Login() {
  const { doLogin } = useApp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://aishwarya-backend-55345914059.asia-northeast1.run.app';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Authenticate against Supabase via the official client
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        throw new Error(authError.message);
      }

      const token = data.session?.access_token;
      if (!token) {
        throw new Error('No access token returned from authentication server');
      }

      localStorage.setItem('backend_url', backendUrl);

      // 2. Verify the session is honored by the backend
      const cleanBackend = backendUrl.replace(/\/$/, '');
      const verifyRes = await fetch(`${cleanBackend}/api/clients`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!verifyRes.ok) {
        let verifyErr = `Backend verification failed (HTTP ${verifyRes.status})`;
        try {
          const errData = await verifyRes.json();
          verifyErr = errData.detail || JSON.stringify(errData);
        } catch (_) {}
        await supabase.auth.signOut();
        throw new Error(`Connected to Supabase successfully, but backend returned error: ${verifyErr}`);
      }

      // 3. Complete login via context action — supabase-js owns token refresh/rotation from here on
      doLogin({
        token,
        email: data.user?.email || email,
        backendUrl,
      });

    } catch (err) {
      console.error('[LOGIN ERROR]', err);
      setError(err.message || 'An unexpected error occurred during sign in');
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-glow"></div>
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <i className="fa-solid fa-print"></i>
          </div>
          <h2>Aishwarya Printers</h2>
          <p>Admin Dashboard Control Panel</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error">
              <i className="fa-solid fa-circle-exclamation"></i>
              <span>{error}</span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address / Username</label>
            <div className="input-with-icon">
              <i className="fa-regular fa-envelope"></i>
              <input
                id="email"
                type="text"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-with-icon">
              <i className="fa-solid fa-lock"></i>
              <input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
              />
            </div>
          </div>

          <button
            type="submit"
            className={`login-submit-btn ${loading ? 'loading' : ''}`}
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i>
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <span>Sign In</span>
                <i className="fa-solid fa-arrow-right-to-bracket"></i>
              </>
            )}
          </button>
        </form>

        <Footer className="login-footer" />
      </div>
    </div>
  );
}
